import { useState } from 'react';
import { Lock, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { useI18n } from '@/hooks/useI18n';
import { ItineraryStatus } from '@/lib/builder/models/itinerary.model';
import { useRevokeQuote } from '@/lib/builder/hooks/useRevokeQuote';

interface PricingLockBannerProps {
    itineraryId?: string;
    status?: ItineraryStatus;
    /** Il cliente ha gia' versato o impegnato denaro: i prezzi restano modificabili, le rate vanno riallineate. */
    hasCommittedPayments: boolean;
    /** Il viaggio e' saldato: qui non si lavora piu', si duplica. */
    isCompleted?: boolean;
    /**
     * Esiste almeno un incasso sull'itinerario.
     *
     * Separato da `hasCommittedPayments` perche' quello comprende anche il tentativo aperto,
     * mentre il ritiro guarda solo l'incasso confermato: revoke_quote rifiuta con
     * itinerary_has_payments solo chi ha davvero incassato.
     */
    hasPayments?: boolean;
    /**
     * C'e' un pagamento avviato e non ancora incassato.
     *
     * Cambia solo il messaggio, ed e' la differenza che al TA interessa di piu': "il cliente
     * ha pagato" e "il cliente sta forse pagando" richiedono due azioni diverse. Nel secondo
     * caso l'importo e' gia' partito verso il cliente con l'IBAN, quindi cambiarlo adesso
     * vuole dire avvisarlo; e il ritiro resta disponibile, se il bonifico non arriva.
     */
    isPaymentInFlight?: boolean;
    /** Chiamata dopo il ritiro: la pagina deve riportare l'itinerario a 'draft'. */
    onRevoked: () => void;
}

/**
 * Un messaggio solo, mai due insieme.
 *
 * A viaggio saldato: il viaggio e' chiuso, l'itinerario si consulta e basta. Per rifare lo
 * stesso viaggio su altre date si duplica, non si riscrive quello vecchio.
 *
 * A preventivo pagato in parte: nessun blocco: si continua a lavorare, prezzi compresi. Il
 * messaggio ricorda l'adempimento che ne consegue — riportare il piano rate sul nuovo totale,
 * perche' e' la somma delle rate che il cliente paga davvero — e l'unico importo intoccabile,
 * cioe' le rate gia' incassate.
 *
 * A preventivo inviato ma non ancora pagato: la possibilita' di ritirarlo, cosi' il
 * viaggiatore non lo vede piu' mentre il TA lo corregge e lo rimanda.
 */
export const PricingLockBanner = ({
    itineraryId,
    status,
    hasCommittedPayments,
    isCompleted = false,
    hasPayments = false,
    isPaymentInFlight = false,
    onRevoked,
}: PricingLockBannerProps) => {
    const { t } = useI18n();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const revoke = useRevokeQuote(itineraryId, () => {
        setIsConfirmOpen(false);
        onRevoked();
    });

    // 'draft' e' l'unico stato in cui il preventivo non e' mai partito: da tutti gli altri
    // il viaggiatore lo ha gia' ricevuto o consultato.
    const isSent = Boolean(status) && status !== 'draft';
    // Dal primo incasso in poi il preventivo non si ritira piu': quello che il cliente ha
    // pagato non puo' sparire dalla sua vista. Un pagamento soltanto *avviato* non conta:
    // congela i prezzi ma lascia il ritiro, che e' l'unico modo di uscirne se non arriva mai.
    const canRevoke = isSent && !hasPayments && !isCompleted;
    // Solo il viaggio saldato e' davvero read-only. Il pagamento in corso d'opera colora il
    // banner ma non chiude niente: e' un promemoria, non un divieto.
    const isLocked = isCompleted;
    const needsAttention = isLocked || hasCommittedPayments;

    if (!itineraryId || (!needsAttention && !canRevoke)) return null;

    // Il messaggio "pending" dice al TA di ritirare il preventivo se il pagamento non
    // arriva: va mostrato solo quando il ritiro è davvero possibile. Con una rata già
    // incassata e un tentativo aperto sulla successiva entrambe le condizioni sono vere, ma
    // `canRevoke` è falso e il pulsante non c'è: l'istruzione sarebbe ineseguibile.
    const message = isCompleted
        ? t('builder.pricingLock.completed')
        : hasCommittedPayments
          ? isPaymentInFlight && !hasPayments
              ? t('builder.pricingLock.pending')
              : t('builder.pricingLock.paid')
          : t('builder.pricingLock.sent');

    return (
        <>
            <div
                className={`flex flex-wrap items-center gap-3 border-b px-4 py-2.5 ${
                    needsAttention ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'
                }`}
            >
                {/* Il lucchetto solo dove qualcosa e' davvero chiuso: a viaggio saldato. Con un
                    pagamento in corso il messaggio e' un promemoria, e disegnarlo come un
                    blocco riporterebbe l'utente esattamente all'equivoco che si vuole togliere. */}
                {isLocked ? (
                    <Lock className="h-4 w-4 flex-shrink-0 text-amber-600" />
                ) : (
                    needsAttention && <Info className="h-4 w-4 flex-shrink-0 text-amber-600" />
                )}
                <p className={`min-w-0 flex-1 text-sm ${needsAttention ? 'text-amber-900' : 'text-gray-600'}`}>
                    {message}
                </p>
                {canRevoke && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 flex-shrink-0 bg-white"
                        onClick={() => setIsConfirmOpen(true)}
                    >
                        {t('builder.pricingLock.actions.revoke')}
                    </Button>
                )}
            </div>

            <Modal
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                size="md"
                title={t('builder.pricingLock.confirmTitle')}
                footer={
                    /* Niente wrapper: il footer del Modal e' gia' un flex allineato a destra
                       con il suo gap, come nelle altre modali di conferma. */
                    <>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsConfirmOpen(false)}
                            disabled={revoke.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button type="button" onClick={() => revoke.mutate()} disabled={revoke.isPending}>
                            {t('builder.pricingLock.actions.confirmRevoke')}
                        </Button>
                    </>
                }
            >
                {/* Il Modal non mette padding sul contenuto: va allineato ai px-6 py-4 di
                    header e footer, altrimenti il testo tocca i bordi del pannello. */}
                <div className="flex flex-col gap-4 px-6 py-4">
                    <p className="text-sm text-gray-700">{t('builder.pricingLock.confirmBody')}</p>

                    <ul className="flex flex-col gap-2">
                        <li className="flex items-start gap-2 text-sm text-gray-600">
                            <span aria-hidden className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                            <span>{t('builder.pricingLock.confirmPoints.keepsData')}</span>
                        </li>
                        <li className="flex items-start gap-2 text-sm text-gray-600">
                            <span aria-hidden className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                            <span>{t('builder.pricingLock.confirmPoints.resend')}</span>
                        </li>
                        {/* Conseguenza che il TA deve conoscere prima di confermare, non dopo:
                            il ritiro annulla il pagamento che il cliente ha avviato. */}
                        {isPaymentInFlight && (
                            <li className="flex items-start gap-2 text-sm text-amber-800">
                                <span aria-hidden className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
                                <span>{t('builder.pricingLock.confirmPoints.closesPending')}</span>
                            </li>
                        )}
                    </ul>
                </div>
            </Modal>
        </>
    );
};
